import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { leadApi, userApi, appointmentApi, estimateApi } from '../lib/api';
import api from '../lib/api';
import { 
  formatDate, formatDateTime, formatCurrency,
  leadStatusLabels, leadStatusColors, 
  leadOpportunityTypeLabels, leadOpportunityTypeColors,
  estimateStatusLabels, estimateStatusColors,
  getBadgeClass 
} from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, User, Phone, Mail, MapPin, Calendar,
  MessageSquare, Clock, Plus, Trash2, FileText, Send, Eye, FolderOpen,
  ChevronRight, Home, HardHat, Briefcase, Sofa, Users, Copy, ChevronDown, RotateCcw, X
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import DocumentManager from '../components/DocumentManager';
import LeadPhasesBar from '../components/leads/LeadPhasesBar';
import LeadDetailPipelinePanel from '../components/leads/LeadDetailPipelinePanel';
import LeadFollowupPanel from '../components/leads/LeadFollowupPanel';
import LeadActivityTimeline from '../components/leads/LeadActivityTimeline';
import CloneEstimateFromExistingDialog from '../components/estimates/CloneEstimateFromExistingDialog';
import { LostReasonDialog } from '../components/leads/LeadMoveDialogs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const CALL_STAGE_LABELS = {
  contattato: 'Contattato', da_richiamare: 'Da Richiamare', da_assegnare: 'Da Assegnare',
  passato_alla_vendita: 'Passato alla Vendita', non_idoneo: 'Non Idoneo',
};
const SALES_STAGE_LABELS = {
  assegnato: 'Nuovo Lead', appuntamento: 'Appuntamento', sopralluogo: 'Sopralluogo',
  preventivazione: 'Preventivazione', presentato: 'Presentato', trattativa: 'Trattativa',
  trattativa_lunga: 'Trattativa Lunga',
  contratto: 'Contratto', convertito: 'Convertito', perso: 'Perso',
};

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isProjectManager, isTechnician, isAdministration, canCreateAppointmentForLead, canAccessLead } = useAuth();
  
  const [lead, setLead] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [workAssignableUsers, setWorkAssignableUsers] = useState([]);
  const [furnitureAssignableUsers, setFurnitureAssignableUsers] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [transitions, setTransitions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAllTimeline, setShowAllTimeline] = useState(false); // Per limitare timeline a 5 eventi
  const [activeTab, setActiveTab] = useState('timeline'); // Tab attiva: timeline o documenti
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [showRecoverModal, setShowRecoverModal] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoverNote, setRecoverNote] = useState('');
  const [showLostModal, setShowLostModal] = useState(false);
  const [markingLost, setMarkingLost] = useState(false);

  // Fase F1.2 · rileva flag dual-pipeline (404 su pipeline-counts = flag OFF → UI legacy)
  const [pipelineEnabled, setPipelineEnabled] = useState(null);
  useEffect(() => {
    let active = true;
    api.get('/leads/pipeline-counts')
      .then(() => active && setPipelineEnabled(true))
      .catch(() => active && setPipelineEnabled(false));
    return () => { active = false; };
  }, []);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    fiscal_code: '', // Codice Fiscale
    // Nuovi campi indirizzo cliente
    customer_address: '',
    customer_city: '',
    customer_cap: '',
    customer_province: '',
    // Nuovi campi indirizzo cantiere
    site_same_as_customer_address: true,
    site_address: '',
    site_city: '',
    site_cap: '',
    site_province: '',
    site_notes: '',
    // Legacy (per retrocompatibilità)
    property_address: '',
    property_city: '',
    property_cap: '',
    source: '',
    status: 'nuovo',
    assigned_technician_id: '',
    // Nuovi campi multi-assegnazione
    lead_opportunity_type: 'non_definito',
    assigned_work_user_id: '',
    assigned_furniture_user_id: ''
  });

  const [appointmentData, setAppointmentData] = useState({
    appointment_date: '',
    appointment_type: 'sopralluogo',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [leadRes, techRes, estimatesRes, workUsersRes, furnitureUsersRes] = await Promise.all([
        leadApi.getOne(id),
        userApi.getTechnicians(),
        estimateApi.getByLead(id),
        fetch('/api/users/assignable?type=work', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()),
        fetch('/api/users/assignable?type=furniture', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json())
      ]);
      
      const leadData = leadRes.data;
      setLead(leadData);
      setTechnicians(techRes.data);
      setWorkAssignableUsers(workUsersRes || []);
      setFurnitureAssignableUsers(furnitureUsersRes || []);
      setEstimates(estimatesRes.data);
      
      // Determina assigned_work_user_id con fallback a legacy
      const effectiveWorkUserId = leadData.assigned_work_user_id || leadData.assigned_technician_id || '';
      
      setFormData({
        first_name: leadData.first_name || '',
        last_name: leadData.last_name || '',
        email: leadData.email || '',
        phone: leadData.phone || '',
        fiscal_code: leadData.fiscal_code || '', // Codice Fiscale
        // Nuovi campi indirizzo cliente (con fallback a legacy)
        customer_address: leadData.customer_address || leadData.property_address || '',
        customer_city: leadData.customer_city || leadData.property_city || '',
        customer_cap: leadData.customer_cap || leadData.property_cap || '',
        customer_province: leadData.customer_province || '',
        // Nuovi campi indirizzo cantiere
        site_same_as_customer_address: leadData.site_same_as_customer_address !== false, // Default true
        site_address: leadData.site_address || '',
        site_city: leadData.site_city || '',
        site_cap: leadData.site_cap || '',
        site_province: leadData.site_province || '',
        site_notes: leadData.site_notes || '',
        // Legacy
        property_address: leadData.property_address || '',
        property_city: leadData.property_city || '',
        property_cap: leadData.property_cap || '',
        source: leadData.source || '',
        status: leadData.status || 'nuovo',
        assigned_technician_id: leadData.assigned_technician_id || '',
        // Nuovi campi multi-assegnazione
        lead_opportunity_type: leadData.lead_opportunity_type || 'non_definito',
        assigned_work_user_id: effectiveWorkUserId,
        assigned_furniture_user_id: leadData.assigned_furniture_user_id || ''
      });

      // Carica dati timeline (non bloccanti)
      loadTimelineData();
    } catch (error) {
      toast.error('Errore nel caricamento del lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  // Carica dati timeline separatamente (non bloccanti)
  const loadTimelineData = async () => {
    try {
      const [transRes, apptRes] = await Promise.all([
        leadApi.getTransitions(id).catch(err => {
          console.warn('[Timeline] Errore caricamento transizioni:', err.message);
          return { data: [] };
        }),
        appointmentApi.getAll({ lead_id: id }).catch(err => {
          console.warn('[Timeline] Errore caricamento appuntamenti:', err.message);
          return { data: [] };
        })
      ]);
      
      setTransitions(transRes.data || []);
      setAppointments(apptRes.data || []);
    } catch (error) {
      console.warn('[Timeline] Errore generico:', error.message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const prevWork = lead?.assigned_work_user_id || lead?.assigned_technician_id || '';
      const newWork = formData.assigned_work_user_id || '';
      const workChanged = newWork !== prevWork;

      // I campi di assegnazione NON passano dal PUT generico (con pipeline-split il PUT
      // rifiuta il cambio tecnico → 409). La riassegnazione ha un endpoint dedicato.
      const { assigned_technician_id: _a, assigned_work_user_id: _b, ...restForm } = formData;
      const updateData = {
        ...restForm,
        email: formData.email || null,
        fiscal_code: formData.fiscal_code || null,
        lead_opportunity_type: formData.lead_opportunity_type || 'non_definito',
        assigned_furniture_user_id: formData.assigned_furniture_user_id || null,
        property_address: formData.customer_address || formData.property_address || '',
        property_city: formData.customer_city || formData.property_city || '',
        property_cap: formData.customer_cap || formData.property_cap || '',
      };
      // OPZIONE C · UNA SOLA mutation per click "Salva" (atomicità sul documento Lead).
      if (workChanged && newWork) {
        if (lead?.sales_stage) {
          // Combined-save atomico: il reassign trasporta anche i normali campi Lead.
          await leadApi.reassign(id, {
            technician_id: newWork,
            reason: 'Cambio Responsabile Lavori dalla scheda',
            updates: updateData,
          });
        } else {
          // Call pipeline: singolo PUT con normali campi + assegnazione.
          await leadApi.update(id, {
            ...updateData,
            assigned_work_user_id: newWork,
            assigned_technician_id: newWork,
          });
        }
      } else {
        await leadApi.update(id, updateData);
      }
      toast.success('Lead aggiornato con successo');
      await loadData();
    } catch (error) {
      const d = error?.response?.data?.detail;
      const msg = typeof d === 'string' ? d
        : Array.isArray(d) ? (d[0]?.msg || 'Dati non validi')
        : (d?.message || 'Errore nel salvataggio');
      toast.error(msg);
      // Error UX: riallinea SEMPRE la UI allo stato reale del DB (nessun valore
      // non salvato lasciato visivamente attivo, nessun partial-success ingannevole).
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async () => {
    setDeleting(true);
    try {
      await leadApi.delete(id);
      toast.success('Lead eliminato');
      navigate('/leads');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleAddNote = async (noteText) => {
    const text = noteText || '';
    if (!text.trim()) return;
    
    try {
      // Call API to add note
      const response = await fetch(`/api/leads/${id}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          withCredentials: true
        },
        body: JSON.stringify({ text: text })
      });
      
      if (!response.ok) throw new Error('Failed to add note');
      
      toast.success('Nota aggiunta');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiunta della nota');
      throw error; // Rilancia per gestire nel componente
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Eliminare questa nota?')) return;
    
    try {
      const response = await fetch(`/api/leads/${id}/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          withCredentials: true
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete note');
      
      toast.success('Nota eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione della nota');
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    
    if (!formData.assigned_technician_id) {
      toast.error('Assegna prima un tecnico al lead');
      return;
    }
    
    try {
      const apptResp = await appointmentApi.create({
        lead_id: id,
        assigned_technician_id: formData.assigned_technician_id,
        appointment_date: new Date(appointmentData.appointment_date).toISOString(),
        appointment_type: appointmentData.appointment_type,
        notes: appointmentData.notes
      });
      
      if (lead?.email && apptResp?.data?.client_email_status === 'failed') {
        toast.warning('Appuntamento creato — invio email al cliente non riuscito');
      } else {
        toast.success('Appuntamento creato');
      }
      setShowAppointmentModal(false);
      setAppointmentData({ appointment_date: '', appointment_type: 'sopralluogo', notes: '' });
      
      // Update lead status
      await leadApi.update(id, { status: 'appuntamento_fissato' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione dell\'appuntamento');
    }
  };

  const getTechnicianName = (techId) => {
    const tech = technicians.find(t => t.id === techId);
    return tech ? `${tech.first_name} ${tech.last_name}` : '-';
  };

  const handleRecoverLead = async () => {
    setRecovering(true);
    try {
      await leadApi.transition(id, {
        new_status: 'nuovo',
        notes: recoverNote.trim() || 'Lead recuperato dallo stato Perso',
      });
      toast.success('Lead recuperato — riportato in stato "Nuovo"');
      setShowRecoverModal(false);
      setRecoverNote('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel recupero del lead');
    } finally {
      setRecovering(false);
    }
  };

  const handleMarkLostConfirm = async (leadId, toStatus, lostFormData) => {
    setMarkingLost(true);
    try {
      await leadApi.transition(id, {
        new_status: 'perso',
        notes: lostFormData?.notes || null,
        motivo_perdita: lostFormData?.reason,
        motivo_perdita_note: lostFormData?.notes || null,
      });
      toast.success('Lead segnato come perso');
      setShowLostModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setMarkingLost(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!lead) return null;

  const notes = Array.isArray(lead.notes) ? lead.notes : [];
  
  // Logica permessi DINAMICI per gestire il lead:
  // Usa la matrice db.permissions + ownership
  // Step 1: canAccessLead verifica se l'utente può accedere al lead
  // Step 2: canCreateAppointmentForLead verifica permesso 'create_appointments' + ownership
  const canManageLead = canAccessLead(lead);
  const canCreateAppointment = canCreateAppointmentForLead(lead);
  const canCreateEstimate = isAdmin || isProjectManager || isAdministration; // Preventivi solo per admin/PM
  
  // === PERMESSI SALVATAGGIO LEAD ===
  // Admin e PM possono sempre salvare
  // Administration può salvare
  // Tecnico può salvare SE assegnato (assigned_technician_id o assigned_work_user_id)
  // Venditore arredi può salvare SE assegnato (assigned_furniture_user_id)
  const isAssignedAsWorkUser = user && (
    lead.assigned_technician_id === user.id ||
    lead.assigned_work_user_id === user.id
  );
  const isAssignedAsFurnitureUser = user && lead.assigned_furniture_user_id === user.id;
  
  const canSaveLead = isAdmin || isProjectManager || isAdministration || 
    (isTechnician && isAssignedAsWorkUser) ||
    (user?.role === 'furniture_sales' && isAssignedAsFurnitureUser);

  // === Fase F1.2 · Dual pipeline UI ===
  const showPipeline = pipelineEnabled === true;
  const pipelineCanManageCall = isAdmin || isProjectManager;
  const pipelineCanManageSales = isAdmin || isProjectManager || (isTechnician && isAssignedAsWorkUser);
  const pipelineHeaderBadge = () => {
    if (lead.sales_stage) return { label: `Vendita: ${SALES_STAGE_LABELS[lead.sales_stage] || lead.sales_stage}`, cls: 'bg-[#FF7819] text-white' };
    if (lead.call_stage) return {
      label: `Chiamata: ${CALL_STAGE_LABELS[lead.call_stage] || lead.call_stage}`,
      cls: lead.call_stage === 'non_idoneo' ? 'bg-rose-600 text-white' : 'bg-cyan-600 text-white',
    };
    return { label: 'Non inizializzato', cls: 'bg-gray-200 text-gray-600' };
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lead-detail-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-[#737373]">
        <Link to="/" className="hover:text-[#FF7819] transition-colors flex items-center gap-1">
          <Home className="w-4 h-4" />
          <span className="hidden sm:inline">Dashboard</span>
        </Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/leads" className="hover:text-[#FF7819] transition-colors">
          Lead
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-[#3C3C3C] font-medium truncate max-w-[200px]">
          {lead.first_name} {lead.last_name}
        </span>
      </nav>

      {/* Header Principale */}
      <div className="bg-white rounded-xl border border-[#EDEDED] p-4 sm:p-6 shadow-sm">
        {/* Mobile: Layout verticale | Desktop: Layout orizzontale */}
        <div className="flex flex-col gap-4 mb-6">
          {/* Riga 1: Avatar + Info */}
          <div className="flex items-start gap-3 sm:gap-4">
            {/* Avatar/Iniziali */}
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-[#FF7819] to-[#FF9F5A] flex items-center justify-center text-white font-bold text-lg sm:text-xl shrink-0">
              {lead.first_name?.[0]}{lead.last_name?.[0]}
            </div>
            
            <div className="flex-1 min-w-0">
              {/* Nome e Badge */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                <h1 className="text-xl sm:text-2xl font-bold text-[#3C3C3C] break-words">
                  {lead.first_name} {lead.last_name}
                </h1>
                {showPipeline ? (
                  (() => {
                    const b = pipelineHeaderBadge();
                    return (
                      <span
                        data-testid="lead-header-pipeline-badge"
                        className={`${b.cls} rounded-full px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-medium`}
                      >
                        {b.label}
                      </span>
                    );
                  })()
                ) : (
                  <span className={`${getBadgeClass(leadStatusColors[lead.status])} px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm`}>
                    {leadStatusLabels[lead.status]}
                  </span>
                )}
              </div>
              
              {/* Info secondarie */}
              <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 text-xs sm:text-sm text-[#737373]">
                {lead.property_city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    {lead.property_city}
                  </span>
                )}
                {lead.source && (
                  <span className="hidden sm:flex items-center gap-1">
                    via {lead.source}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="hidden sm:inline">Creato il</span> {formatDate(lead.created_at)}
                </span>
              </div>
            </div>
          </div>

          {/* Riga 2: Azioni - Grid ordinata */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Azioni contatto (sempre visibili) */}
            <div className="flex items-center gap-2">
              {lead.phone && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(`tel:${lead.phone}`, '_self')}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50 h-9"
                >
                  <Phone className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Chiama</span>
                </Button>
              )}
              {lead.email && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(`mailto:${lead.email}`, '_self')}
                  className="text-green-600 border-green-200 hover:bg-green-50 h-9"
                >
                  <Mail className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Email</span>
                </Button>
              )}
            </div>

            {/* Separatore visivo su desktop */}
            <div className="hidden lg:block w-px h-6 bg-gray-200" />

            {/* Azioni gestione */}
            <div className="flex items-center gap-2">
              {canCreateAppointment && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAppointmentModal(true)}
                  data-testid="create-appointment-btn"
                  className="h-9"
                >
                  <Calendar className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Appuntamento</span>
                </Button>
              )}
              {isAdmin && (
                <Button 
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 h-9"
                  onClick={() => setShowDeleteModal(true)}
                  data-testid="delete-lead-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}

              {/* Recupera lead perso (visibile solo se stato='perso') */}
              {lead.status === 'perso' && (isAdmin || isProjectManager) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 border-emerald-300 h-9"
                  onClick={() => setShowRecoverModal(true)}
                  data-testid="recover-lead-btn"
                  title="Riporta il lead nella pipeline attiva"
                >
                  <RotateCcw className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Recupera</span>
                </Button>
              )}
            </div>

            {/* Spacer per spingere Salva a destra su desktop */}
            <div className="hidden lg:flex flex-1" />

            {/* Pulsante Salva (sempre ultimo, evidenziato) */}
            {canSaveLead && (
              <Button 
                className="btn-primary h-9 ml-auto sm:ml-0" 
                size="sm"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-lead-btn"
              >
                <Save className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">{saving ? 'Salvo...' : 'Salva'}</span>
              </Button>
            )}
          </div>
        </div>

        {/* Barra Fasi Lead (legacy) — nascosta con dual-pipeline attivo */}
        {!showPipeline && (
          <LeadPhasesBar 
            currentStatus={lead.status} 
            createdAt={lead.created_at}
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Info */}
          <Card className="card-abitalo">
            <CardHeader className="pb-4">
              <CardTitle className="text-h4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#FF7819]" />
                Dati Personali
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="input-abitalo"
                    data-testid="lead-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cognome *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="input-abitalo"
                    data-testid="lead-last-name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telefono *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-abitalo pl-10"
                      data-testid="lead-phone"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-abitalo pl-10"
                      data-testid="lead-email"
                    />
                  </div>
                </div>
              </div>
              
              {/* Codice Fiscale */}
              <div className="space-y-2">
                <Label>Codice Fiscale</Label>
                <Input
                  value={formData.fiscal_code}
                  onChange={(e) => setFormData({ ...formData, fiscal_code: e.target.value.toUpperCase() })}
                  className="input-abitalo"
                  placeholder="RSSMRA80A01H501U"
                  maxLength={16}
                  data-testid="lead-fiscal-code"
                />
              </div>
            </CardContent>
          </Card>

          {/* === BLOCCO A: Indirizzo Cliente (Residenza/Domicilio) === */}
          <Card className="card-abitalo">
            <CardHeader className="pb-4">
              <CardTitle className="text-h4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#FF7819]" />
                Indirizzo Cliente (Residenza/Domicilio)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Indirizzo *</Label>
                <Input
                  value={formData.customer_address}
                  onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                  className="input-abitalo"
                  placeholder="Es. Via Roma 123"
                  data-testid="lead-customer-address"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Città</Label>
                  <Input
                    value={formData.customer_city}
                    onChange={(e) => setFormData({ ...formData, customer_city: e.target.value })}
                    className="input-abitalo"
                    data-testid="lead-customer-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CAP</Label>
                  <Input
                    value={formData.customer_cap}
                    onChange={(e) => setFormData({ ...formData, customer_cap: e.target.value })}
                    className="input-abitalo"
                    data-testid="lead-customer-cap"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Provincia</Label>
                  <Input
                    value={formData.customer_province}
                    onChange={(e) => setFormData({ ...formData, customer_province: e.target.value.toUpperCase() })}
                    className="input-abitalo"
                    maxLength={2}
                    placeholder="BA"
                    data-testid="lead-customer-province"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* === BLOCCO B: Indirizzo Cantiere (Immobile oggetto lavori) === */}
          <Card className="card-abitalo">
            <CardHeader className="pb-4">
              <CardTitle className="text-h4 flex items-center gap-2">
                <HardHat className="w-5 h-5 text-[#FF7819]" />
                Indirizzo Cantiere (Immobile oggetto lavori)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Checkbox coincidenza */}
              <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-md">
                <Checkbox
                  id="site-same-as-customer-detail"
                  checked={formData.site_same_as_customer_address}
                  onCheckedChange={(checked) => {
                    setFormData(prev => ({
                      ...prev,
                      site_same_as_customer_address: checked,
                      // Se attivo, pulisci campi cantiere
                      ...(checked ? {
                        site_address: '',
                        site_city: '',
                        site_cap: '',
                        site_province: '',
                        site_notes: ''
                      } : {})
                    }));
                  }}
                  data-testid="lead-site-same-checkbox"
                />
                <label
                  htmlFor="site-same-as-customer-detail"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Il cantiere coincide con l'indirizzo di residenza del cliente
                </label>
              </div>
              
              {/* Indicazione se coincidono */}
              {formData.site_same_as_customer_address && formData.customer_address && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                  <MapPin className="inline w-4 h-4 mr-1" />
                  Cantiere: {formData.customer_address}
                  {formData.customer_city && `, ${formData.customer_city}`}
                  {formData.customer_cap && ` ${formData.customer_cap}`}
                  {formData.customer_province && ` (${formData.customer_province})`}
                </div>
              )}
              
              {/* Campi cantiere (visibili solo se checkbox disattivo) */}
              {!formData.site_same_as_customer_address && (
                <>
                  <div className="space-y-2">
                    <Label>Indirizzo Cantiere *</Label>
                    <Input
                      value={formData.site_address}
                      onChange={(e) => setFormData({ ...formData, site_address: e.target.value })}
                      className="input-abitalo"
                      placeholder="Es. Via Napoli 45"
                      data-testid="lead-site-address"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Città</Label>
                      <Input
                        value={formData.site_city}
                        onChange={(e) => setFormData({ ...formData, site_city: e.target.value })}
                        className="input-abitalo"
                        data-testid="lead-site-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CAP</Label>
                      <Input
                        value={formData.site_cap}
                        onChange={(e) => setFormData({ ...formData, site_cap: e.target.value })}
                        className="input-abitalo"
                        data-testid="lead-site-cap"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Provincia</Label>
                      <Input
                        value={formData.site_province}
                        onChange={(e) => setFormData({ ...formData, site_province: e.target.value.toUpperCase() })}
                        className="input-abitalo"
                        maxLength={2}
                        placeholder="BA"
                        data-testid="lead-site-province"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Note accesso cantiere</Label>
                    <Textarea
                      value={formData.site_notes}
                      onChange={(e) => setFormData({ ...formData, site_notes: e.target.value })}
                      placeholder="Es. Citofono Rossi, portone sulla destra, piano 3..."
                      rows={2}
                      data-testid="lead-site-notes"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Preventivi del Lead - SOPRA ATTIVITÀ/DOCUMENTI */}
          <Card className="card-abitalo" data-testid="lead-estimates-section">
            <CardHeader className="border-b border-[#DCDCDC] py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-h4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#FF7819]" />
                  Preventivi ({estimates.length})
                </CardTitle>
                {(isAdmin || isProjectManager || isTechnician) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" className="btn-primary" data-testid="create-estimate-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Nuovo Preventivo
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem
                        onClick={() => navigate(`/estimates/new?lead_id=${id}`)}
                        data-testid="create-estimate-from-scratch"
                      >
                        <FileText className="w-4 h-4 mr-2 text-[#FF7819]" />
                        <div>
                          <div className="font-medium">Crea da zero</div>
                          <div className="text-xs text-[#737373]">Nuovo preventivo vuoto</div>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setShowCloneDialog(true)}
                        data-testid="create-estimate-from-existing"
                      >
                        <Copy className="w-4 h-4 mr-2 text-[#FF7819]" />
                        <div>
                          <div className="font-medium">Usa preventivo esistente</div>
                          <div className="text-xs text-[#737373]">Clona da un altro lead</div>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {estimates.length === 0 ? (
                <div className="p-6 text-center text-[#737373]">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Nessun preventivo per questo lead</p>
                  {(isAdmin || isProjectManager) && (
                    <div className="flex flex-wrap gap-2 justify-center mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/estimates/new?lead_id=${id}`)}
                        data-testid="empty-create-scratch"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Crea da zero
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCloneDialog(true)}
                        data-testid="empty-create-from-existing"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Usa esistente
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-[#DCDCDC]">
                  {estimates.map((est) => (
                    <div 
                      key={est.id}
                      className="p-4 hover:bg-[#FAFAFA] cursor-pointer transition-colors group"
                      onClick={() => navigate(`/estimates/${est.id}`)}
                      data-testid={`estimate-row-${est.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-[#3C3C3C]">{est.estimate_number}</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${getBadgeClass(estimateStatusColors[est.status])}`}>
                              {estimateStatusLabels[est.status]}
                            </span>
                          </div>
                          <p className="text-xs text-[#737373] mt-1">
                            Creato il {formatDate(est.created_at)} • {est.items?.length || 0} voci
                          </p>
                        </div>
                        <div className="text-right flex items-center gap-3">
                          <p className="font-bold text-[#FF7819] text-lg">{formatCurrency(est.total)}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-[#737373] hover:text-[#FF7819] hover:border-[#FF7819] opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/estimates/${est.id}`);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Apri
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attività e Documenti - Card unica con tabs */}
          <Card className="card-abitalo" data-testid="lead-activity-documents-section">
            <CardHeader className="py-3 border-b border-[#DCDCDC]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-h4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#FF7819]" />
                  Attività e Documenti
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="w-full grid grid-cols-2 h-auto p-1 bg-gray-100 rounded-none border-b">
                  <TabsTrigger 
                    value="timeline" 
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm py-2"
                    data-testid="tab-timeline"
                  >
                    <Clock className="w-4 h-4 mr-1.5" />
                    Timeline
                  </TabsTrigger>
                  <TabsTrigger 
                    value="documenti" 
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm py-2"
                    data-testid="tab-documenti"
                  >
                    <FolderOpen className="w-4 h-4 mr-1.5" />
                    Documenti
                  </TabsTrigger>
                </TabsList>
                
                {/* Tab Timeline */}
                <TabsContent value="timeline" className="p-4 mt-0">
                  <LeadActivityTimeline
                    notes={notes}
                    transitions={transitions}
                    appointments={appointments}
                    estimates={estimates}
                    onAddNote={handleAddNote}
                    onDeleteNote={handleDeleteNote}
                    canEdit={isAdmin || isProjectManager}
                    maxEvents={showAllTimeline ? undefined : 5}
                    compact={true}
                  />
                  {/* Pulsante Mostra tutto / Mostra meno */}
                  {(notes.length + transitions.length + appointments.length + estimates.length) > 5 && (
                    <div className="mt-4 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllTimeline(!showAllTimeline)}
                        className="text-[#FF7819] hover:text-[#CC5F14] hover:bg-orange-50"
                        data-testid="toggle-timeline-btn"
                      >
                        {showAllTimeline ? 'Mostra meno attività' : 'Mostra tutta la timeline'}
                      </Button>
                    </div>
                  )}
                </TabsContent>
                
                {/* Tab Documenti */}
                <TabsContent value="documenti" className="p-4 mt-0">
                  <DocumentManager 
                    customerId={id}
                    title=""
                    showHeader={false}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stato Lead — dual-pipeline (flag ON) oppure legacy (flag OFF) */}
          {showPipeline ? (
            <LeadDetailPipelinePanel
              lead={lead}
              technicians={technicians}
              assignableWorkUsers={workAssignableUsers}
              transitions={transitions}
              canManageCall={pipelineCanManageCall}
              canManageSales={pipelineCanManageSales}
              onChanged={loadData}
            />
          ) : (
            <Card className="card-abitalo">
              <CardHeader className="pb-4">
                <CardTitle className="text-h4">Stato Lead</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Stato</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => {
                      if (value === 'perso' && lead?.status !== 'perso') {
                        // Intercetta: apri modale motivo perdita invece di cambiare direttamente.
                        // Il cambio effettivo passa dall'endpoint /transition per registrare il motivo.
                        setShowLostModal(true);
                        return;
                      }
                      setFormData({ ...formData, status: value });
                    }}
                    disabled={isTechnician}
                  >
                    <SelectTrigger data-testid="lead-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(leadStatusLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Follow-up commerciale V3 (§21) */}
          <LeadFollowupPanel leadId={id} onChange={loadTimelineData} />
          
          {/* Assegnazioni - NUOVA SEZIONE */}
          <Card className="card-abitalo">
            <CardHeader className="pb-4">
              <CardTitle className="text-h4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FF7819]" />
                Assegnazioni
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tipo Opportunità */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-[#737373]" />
                  Tipo opportunità
                </Label>
                <Select
                  value={formData.lead_opportunity_type || 'non_definito'}
                  onValueChange={(value) => setFormData({ ...formData, lead_opportunity_type: value })}
                  disabled={isTechnician}
                >
                  <SelectTrigger data-testid="lead-opportunity-type-select">
                    <SelectValue placeholder="Seleziona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(leadOpportunityTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.lead_opportunity_type && formData.lead_opportunity_type !== 'non_definito' && (
                  <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    formData.lead_opportunity_type === 'lavori' ? 'bg-[#E5F0FF] text-[#0078C8]' :
                    formData.lead_opportunity_type === 'arredi' ? 'bg-[#E5F7ED] text-[#008A3D]' :
                    formData.lead_opportunity_type === 'lavori_arredi' ? 'bg-[#FFF9E5] text-[#B38C00]' :
                    'bg-[#F5F5F5] text-[#737373]'
                  }`}>
                    {formData.lead_opportunity_type === 'lavori' && <HardHat className="w-3 h-3" />}
                    {formData.lead_opportunity_type === 'arredi' && <Sofa className="w-3 h-3" />}
                    {formData.lead_opportunity_type === 'lavori_arredi' && <><HardHat className="w-3 h-3" /><Sofa className="w-3 h-3" /></>}
                    {leadOpportunityTypeLabels[formData.lead_opportunity_type]}
                  </div>
                )}
              </div>

              <Separator />

              {/* Responsabile Lavori */}
              {(formData.lead_opportunity_type === 'non_definito' || 
                formData.lead_opportunity_type === 'lavori' || 
                formData.lead_opportunity_type === 'lavori_arredi') && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <HardHat className="w-4 h-4 text-[#0078C8]" />
                    Responsabile Lavori
                  </Label>
                  <Select
                    value={formData.assigned_work_user_id || "none"}
                    onValueChange={(value) => {
                      setFormData({ 
                        ...formData, 
                        assigned_work_user_id: value === "none" ? "" : value,
                        // Sync anche con legacy per retrocompatibilità
                        assigned_technician_id: value === "none" ? "" : value 
                      });
                    }}
                    disabled={isTechnician}
                  >
                    <SelectTrigger data-testid="lead-work-user-select">
                      <SelectValue placeholder="Seleziona responsabile" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non assegnato</SelectItem>
                      {workAssignableUsers.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                          <span className="text-xs text-[#737373] ml-2">
                            ({user.role === 'project_manager' ? 'PM' : 
                              user.role === 'external_technician' ? 'Tecnico' : 
                              user.role === 'admin' ? 'Admin' : user.role})
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.assigned_work_user_id && (
                    <div className="p-3 bg-[#E5F0FF] rounded-lg border border-[#B3D4F5]">
                      <p className="text-xs text-[#0078C8] font-medium flex items-center gap-1">
                        <HardHat className="w-3 h-3" />
                        Responsabile Lavori
                      </p>
                      <p className="text-sm text-[#3C3C3C] font-medium">
                        {workAssignableUsers.find(u => u.id === formData.assigned_work_user_id)?.full_name ||
                         workAssignableUsers.find(u => u.id === formData.assigned_work_user_id)?.email ||
                         getTechnicianName(formData.assigned_work_user_id)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Responsabile Arredi */}
              {(formData.lead_opportunity_type === 'non_definito' || 
                formData.lead_opportunity_type === 'arredi' || 
                formData.lead_opportunity_type === 'lavori_arredi') && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Sofa className="w-4 h-4 text-[#008A3D]" />
                    Responsabile Arredi
                  </Label>
                  <Select
                    value={formData.assigned_furniture_user_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, assigned_furniture_user_id: value === "none" ? "" : value })}
                    disabled={isTechnician}
                  >
                    <SelectTrigger data-testid="lead-furniture-user-select">
                      <SelectValue placeholder="Seleziona responsabile" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non assegnato</SelectItem>
                      {furnitureAssignableUsers.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                          <span className="text-xs text-[#737373] ml-2">
                            ({user.role === 'furniture_sales' ? 'Arredi' : 
                              user.role === 'admin' ? 'Admin' : user.role})
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.assigned_furniture_user_id && (
                    <div className="p-3 bg-[#E5F7ED] rounded-lg border border-[#B3EBD0]">
                      <p className="text-xs text-[#008A3D] font-medium flex items-center gap-1">
                        <Sofa className="w-3 h-3" />
                        Responsabile Arredi
                      </p>
                      <p className="text-sm text-[#3C3C3C] font-medium">
                        {furnitureAssignableUsers.find(u => u.id === formData.assigned_furniture_user_id)?.full_name ||
                         furnitureAssignableUsers.find(u => u.id === formData.assigned_furniture_user_id)?.email ||
                         'Non trovato'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="card-abitalo">
            <CardHeader className="pb-4">
              <CardTitle className="text-h4">Azioni Rapide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {canCreateAppointment && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setShowAppointmentModal(true)}
                >
                  <Calendar className="w-4 h-4 mr-2 text-[#0078C8]" />
                  Pianifica Appuntamento
                </Button>
              )}
              {canCreateEstimate && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/estimates/new?lead_id=${id}`)}
                >
                  <FileText className="w-4 h-4 mr-2 text-[#FF7819]" />
                  Crea Preventivo
                </Button>
              )}
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => window.open(`tel:${lead.phone}`)}
              >
                <Phone className="w-4 h-4 mr-2 text-[#008A3D]" />
                Chiama Cliente
              </Button>
              {lead.email && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => window.open(`mailto:${lead.email}`)}
                >
                  <Mail className="w-4 h-4 mr-2 text-[#B38C00]" />
                  Invia Email
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Provenienza - SPOSTATA NELLA COLONNA DESTRA */}
          <Card className="card-abitalo">
            <CardHeader className="pb-3">
              <CardTitle className="text-h4 flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-[#FF7819]" />
                Provenienza
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                <Label className="text-xs text-[#737373]">Fonte Lead</Label>
                <Input
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  placeholder="Es: Sito web, Passaparola, Facebook..."
                  className="input-abitalo h-9 text-sm"
                  data-testid="lead-source-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Info Card - Suggerimento compatto */}
          <Card className="card-abitalo bg-[#FFF0E5] border-[#FFD1B3]">
            <CardContent className="p-3">
              <p className="text-xs text-[#CC5900]">
                <strong>Suggerimento:</strong> Aggiungi note per tracciare le comunicazioni con il cliente.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Appointment Modal */}
      <Dialog open={showAppointmentModal} onOpenChange={setShowAppointmentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-h3">Nuovo Appuntamento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAppointment} className="space-y-4">
            <div className="space-y-2">
              <Label>Data e Ora *</Label>
              <Input
                type="datetime-local"
                value={appointmentData.appointment_date}
                onChange={(e) => setAppointmentData({ ...appointmentData, appointment_date: e.target.value })}
                className="input-abitalo"
                required
                data-testid="appointment-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo Appuntamento</Label>
              <Select
                value={appointmentData.appointment_type}
                onValueChange={(value) => setAppointmentData({ ...appointmentData, appointment_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sopralluogo">Sopralluogo</SelectItem>
                  <SelectItem value="incontro_sede">Incontro in Sede</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={appointmentData.notes}
                onChange={(e) => setAppointmentData({ ...appointmentData, notes: e.target.value })}
                className="input-abitalo min-h-[80px]"
                placeholder="Note per l'appuntamento..."
              />
            </div>

            {!formData.assigned_technician_id && (
              <div className="p-3 bg-[#FFF9E5] rounded-lg border border-[#FFE999]">
                <p className="text-sm text-[#B38C00]">
                  Attenzione: Prima assegna un tecnico al lead per creare l'appuntamento.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAppointmentModal(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="btn-primary"
                disabled={!formData.assigned_technician_id || !appointmentData.appointment_date}
                data-testid="submit-appointment-btn"
              >
                Crea Appuntamento
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Lead Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Elimina Lead
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Sei sicuro di voler eliminare questo lead? Verranno eliminati anche:
            </p>
            <ul className="text-sm text-gray-500 list-disc list-inside space-y-1">
              <li>Tutti gli appuntamenti collegati</li>
              <li>Tutti i preventivi</li>
              <li>Tutti i contratti</li>
              <li>Tutti i documenti</li>
            </ul>
            <p className="mt-4 font-medium text-red-600">Questa azione è irreversibile.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteLead}
              disabled={deleting}
              data-testid="confirm-delete-lead-btn"
            >
              {deleting ? 'Eliminazione...' : 'Elimina Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clone Estimate From Existing Dialog */}
      <CloneEstimateFromExistingDialog
        destinationLeadId={id}
        destinationLeadName={lead ? `${lead.first_name} ${lead.last_name}`.trim() : ''}
        open={showCloneDialog}
        onClose={() => setShowCloneDialog(false)}
      />

      {/* Modale Motivo Perdita (Segna come Perso) */}
      <LostReasonDialog
        open={showLostModal}
        onOpenChange={(open) => !open && setShowLostModal(false)}
        lead={lead}
        onConfirm={handleMarkLostConfirm}
        isSubmitting={markingLost}
      />

      {/* Modale Recupera Lead */}
      <Dialog open={showRecoverModal} onOpenChange={setShowRecoverModal}>
        <DialogContent className="max-w-md" data-testid="recover-lead-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-emerald-700" />
              Recupera lead dallo stato Perso
            </DialogTitle>
            <DialogDescription>
              Il lead <strong>{lead?.first_name} {lead?.last_name}</strong> verrà
              riportato in stato <strong>Nuovo</strong> e tornerà nella pipeline
              commerciale. Preventivi, note, appuntamenti e assegnazioni restano
              intatti.
            </DialogDescription>
          </DialogHeader>

          {/* Riquadro motivo perdita (se presente) */}
          {lead?.motivo_perdita && (
            <div
              className="rounded-lg border border-red-200 bg-red-50/60 p-3 text-sm text-red-900"
              data-testid="recover-lead-motivo-box"
            >
              <div className="font-semibold flex items-center gap-1.5">
                <X className="w-4 h-4" />
                Motivo perdita registrato
              </div>
              <div className="mt-1">
                <span className="font-medium">
                  {lead.motivo_perdita_label || lead.motivo_perdita}
                </span>
                {lead.motivo_perdita_note && (
                  <p className="mt-1 text-red-800/80 whitespace-pre-line">
                    {lead.motivo_perdita_note}
                  </p>
                )}
                {lead.perso_at && (
                  <p className="mt-1 text-xs text-red-700/70">
                    Perso il {formatDate(lead.perso_at)}
                    {lead.perso_by_user_name ? ` da ${lead.perso_by_user_name}` : ''}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label htmlFor="recover-note" className="text-sm">
                Motivo del recupero (opzionale)
              </Label>
              <Textarea
                id="recover-note"
                value={recoverNote}
                onChange={(e) => setRecoverNote(e.target.value)}
                placeholder="Es. Cliente ha richiamato, ha chiesto di riprendere il progetto..."
                rows={3}
                className="mt-1"
                data-testid="recover-note-input"
              />
              <p className="text-xs text-[#737373] mt-1">
                Verrà salvato nella cronologia del lead come nota di transizione.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRecoverModal(false)}
              disabled={recovering}
              data-testid="recover-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleRecoverLead}
              disabled={recovering}
              data-testid="recover-confirm-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {recovering ? 'Recupero...' : 'Conferma recupero'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadDetailPage;
